import Button from 'ext-ui/components/Button/Button'
import InlineConfirmationPanel from 'ext-ui/components/InlineConfirmationPanel/InlineConfirmationPanel'
import React, { useEffect, useRef, useState } from 'react'
import SvgMore from 'src/components/Svgs/SvgMore'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'
import { convertEnumToSentenceCase } from 'src/utils/string.utils'

import * as style from './PaymentInformationTab.style'
import { UseVendorPaymentMethodResult } from './useVendorPaymentMethod'
import { footerError, pendingNote, visuallyHidden } from './VendorFlyout.style'

const EM_DASH = '—'

/** Render a nullable/blank wire field as the prototype's em-dash, never
 * `undefined`. */
const orDash = (value: string | null | undefined): string =>
  value == null || value === '' ? EM_DASH : value

type TranslateFn = (key: string) => string

/**
 * The card's kebab → single destructive "Remove account" item, following the
 * WAI-ARIA menu-button pattern: `aria-haspopup`, `aria-expanded`, focus into
 * the item on open, Escape / outside-click close, focus returned to the trigger
 * on Escape. Opening the menu does nothing on its own — removal is gated behind
 * the confirm the item opens.
 */
const AccountActionsMenu: React.FC<{
  onRemove: () => void
  translate: TranslateFn
}> = ({ onRemove, translate }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRef = useRef<HTMLButtonElement | null>(null)
  const menuId = 'vendor-payment-actions-menu'

  useEffect(() => {
    if (!open) return undefined
    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return (): void => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (open) itemRef.current?.focus()
  }, [open])

  return (
    <div css={style.menuWrap}>
      <button
        ref={triggerRef}
        type={'button'}
        css={style.kebabTrigger}
        aria-label={translate('Account actions')}
        aria-haspopup={'menu'}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={'vendor-payment-actions'}
        onClick={() => setOpen((value) => !value)}
      >
        <SvgMore width={20} height={20} viewBox={'0 0 24 24'} />
      </button>
      {open && (
        <div
          ref={menuRef}
          role={'menu'}
          id={menuId}
          css={style.menu}
          aria-label={translate('Account actions')}
        >
          <button
            ref={itemRef}
            type={'button'}
            role={'menuitem'}
            css={[style.menuItem, style.menuItemSevere]}
            data-testid={'vendor-payment-remove'}
            onClick={() => {
              setOpen(false)
              // The item unmounts on close, so without this focus falls to
              // <body> and a keyboard user has to tab from the top of the
              // document to reach the confirm that just appeared below.
              triggerRef.current?.focus()
              onRemove()
            }}
          >
            {translate('Remove account')}
          </button>
        </div>
      )}
    </div>
  )
}

export interface PaymentInformationTabProps {
  id: string
  labelledBy: string
  /**
   * Falsy when the tab is editable; otherwise WHY it is not. Read-only covers
   * two different situations that need the same gate but different copy — an
   * archived vendor, and a viewer without edit rights (EX-40761's `editEnabled`)
   * — so the reason travels with the gate rather than being re-derived here. A
   * plain boolean is what let the empty state tell a live vendor it was
   * archived.
   */
  readOnly: false | 'archived' | 'view-only'
  payment: UseVendorPaymentMethodResult
}

/**
 * The Payment Information subtab (EX-40772). Two states, selected by the single
 * `payment.hasCurrentAccount` predicate off the vendor detail — never by the
 * bank read:
 *
 * - State A — a current ACH account: the summary card + kebab → confirm →
 *   retire. A provider read failure (502) stays State A with an error affordance
 *   and em-dashes, never the embed.
 * - State B — no current account: the reused Modern Treasury embed → link →
 *   (acknowledgement + Confirm and Add live in the shell footer).
 *
 * The ACH acknowledgement and its confirm action are contributed into the
 * shell's single shared footer (see VendorEditLoaded), not rendered here — this
 * tab never owns a footer of its own.
 */
const PaymentInformationTab: React.FC<PaymentInformationTabProps> = ({
  id,
  labelledBy,
  readOnly,
  payment,
}) => {
  const translate = useTranslate()

  const details = payment.details
  const bankRows: { label: string; value: string }[] = [
    { label: translate('Bank Name'), value: orDash(details?.bankName) },
    {
      label: translate('Account Type'),
      // Modern Treasury returns this lower-cased (`checking` / `savings`) and
      // extend-api passes it through verbatim, so sentence-case it for display.
      // `&&` short-circuits null/undefined/'' straight into orDash's em-dash.
      value: orDash(
        details?.accountType && convertEnumToSentenceCase(details.accountType),
      ),
    },
    {
      label: translate('Routing Number'),
      value: orDash(details?.routingNumber),
    },
    {
      label: translate('Account Number'),
      value:
        details?.accountNumberLast4 == null
          ? EM_DASH
          : `•••• ${details.accountNumberLast4}`,
    },
  ]

  return (
    <div
      css={style.tabBody}
      id={id}
      role={'tabpanel'}
      aria-labelledby={labelledBy}
      tabIndex={-1}
      data-testid={'vendor-edit-payment-tab'}
    >
      {payment.hasCurrentAccount ? (
        <>
          <section css={style.card} data-testid={'vendor-ach-account-card'}>
            <div css={style.cardHeader}>
              <h3 css={style.cardTitle}>{translate('Vendor ACH Account')}</h3>
              {!readOnly && (
                <AccountActionsMenu
                  onRemove={payment.openRemoveConfirm}
                  translate={translate}
                />
              )}
            </div>

            {payment.isDetailsLoading && !details ? (
              <p css={style.sectionNote} role={'status'} aria-live={'polite'}>
                {translate('Loading bank details…')}
              </p>
            ) : (
              <>
                {payment.isDetailsError && (
                  // A provider (502) read failure is an error state, not a
                  // blank card, and never the embed — the account still
                  // exists. The rows stay, so the card still reads as an
                  // account (em-dashed) with an explanation above it, rather
                  // than collapsing to a lone error line.
                  <p css={footerError} role={'alert'}>
                    {translate(
                      "We couldn't load this account's bank details right now. Please try again.",
                    )}
                  </p>
                )}
                <div css={style.rows}>
                  {bankRows.map(({ label, value }) => (
                    <div css={style.row} key={label}>
                      <span css={style.rowLabel}>{label}</span>
                      <span css={style.rowValue}>{value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* EX-40772's in-progress payment disclosure, in its generic form: a
              payment past MFA carries its own copy of the account it was
              authorized against and settles there regardless, so the user is
              told that plainly rather than being shown a per-payment amount +
              destination last-four (which would cost a payment-history call
              plus N detail reads at two Modern Treasury round-trips each).
              Pairs with the remove confirm, which covers the other half:
              payments still AWAITING approval are cancelled, not carried. */}
          <p
            css={style.sectionNote}
            data-testid={'vendor-payment-in-flight-note'}
          >
            {translate(
              "Any in-progress payments will continue to the vendor's account at the time the payment was authorized.",
            )}
          </p>

          {payment.removeError && (
            <p css={footerError} role={'alert'}>
              {payment.removeError}
            </p>
          )}

          {payment.isConfirmingRemoval && (
            // The panel carries a material consequence (it also cancels
            // payments awaiting approval), and ext-ui's InlineConfirmationPanel
            // ships no role of its own, so announce it where it is used.
            <div role={'alert'}>
              <InlineConfirmationPanel
                message={translate(
                  "Remove ACH details? This vendor's ACH account details will be cleared and any payment to this vendor still waiting for approval will be cancelled. You'll need to re-enter the details to add a payment method again.",
                )}
                confirmLabel={translate('Remove')}
                onCancel={payment.cancelRemoveConfirm}
                onConfirm={payment.confirmRemoval}
              />
            </div>
          )}
        </>
      ) : readOnly ? (
        // Read-only vendor with no current account. The shell hides the footer
        // here, so rendering the collection embed would walk the user through
        // creating a real external account at Modern Treasury with no Confirm
        // and Add to finish it — a dead end that leaves an orphaned account
        // behind. Read-only means read-only, whatever the reason.
        <p
          css={style.sectionNote}
          data-testid={
            readOnly === 'archived'
              ? 'vendor-payment-archived-note'
              : 'vendor-payment-view-only-note'
          }
        >
          {readOnly === 'archived'
            ? translate(
                'This vendor is archived. Restore it to add ACH payment details.',
              )
            : translate(
                'You do not have permission to change ACH payment details for this vendor.',
              )}
        </p>
      ) : payment.hasOpenFlowConflict ? (
        // One-open-flow: a just-retired vendor still held a stale open flow, so
        // starting a new one 409'd. Offer a retry, not a dead end.
        <div data-testid={'vendor-payment-flow-conflict'}>
          <p css={footerError} role={'alert'}>
            {translate(
              "We couldn't start a new bank account setup because an earlier one is still open. Please try again.",
            )}
          </p>
          <Button
            variant={'secondary'}
            type={'button'}
            onClick={payment.retryReAdd}
            data-testid={'vendor-payment-flow-retry'}
          >
            {translate('Try again')}
          </Button>
        </div>
      ) : payment.phase === 'collecting' ? (
        <>
          <p css={style.sectionNote}>
            {translate('Add a new ACH account for this vendor.')}
          </p>
          <MtAccountCollection
            key={payment.embedKey}
            createFlow={payment.createFlowForEmbed}
            completeFlow={payment.completeFlowForEmbed}
            onLinked={payment.handleCollectionLinked}
            showHeading={false}
            flush
          />
        </>
      ) : (
        // Phase B — Modern Treasury accepted the hosted form. The acknowledgement
        // checkbox + Confirm and Add live in the shell footer; this body carries
        // only the status / error surfaces for the confirm sequence.
        <div role={'group'} aria-label={translate('Confirm ACH account')}>
          {payment.phase === 'confirmation-pending' && (
            <p css={pendingNote} role={'status'} aria-live={'polite'}>
              {payment.confirmation.canRetry
                ? translate(
                    'This bank account is still being confirmed. Try again to check its status.',
                  )
                : translate(
                    'This bank account is still being confirmed. This can take a moment.',
                  )}
            </p>
          )}
          {payment.phase === 'confirmation-error' &&
            payment.confirmationErrorMessage && (
              <p css={footerError} role={'alert'}>
                {payment.confirmationErrorMessage}
              </p>
            )}
          {payment.phase === 'confirming' && (
            <p css={visuallyHidden} role={'status'} aria-live={'polite'}>
              {translate('Updating payment details…')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default PaymentInformationTab
